import React, { useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField, Typography } from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import { User } from '../../api/users';
import { formatDateWithTime } from '../../utils/dateUtils';

interface DeleteUserDialogProps {
  open: boolean;
  user: User | null;
  onClose: () => void;
  onConfirm: (userId: string) => Promise<void>;
}

const DeleteUserDialog: React.FC<DeleteUserDialogProps> = ({ open, user, onClose, onConfirm }) => {
  const [ confirmText, setConfirmText ] = useState('');
  const [ isDeleting, setIsDeleting ] = useState(false);
  const [ error, setError ] = useState<string | null>(null);
  const handleClose = () => {
    setConfirmText('');
    setError(null);
    onClose();
  };
  const handleDelete = async() => {
    if (!user || confirmText !== '削除する') return;
    setIsDeleting(true);
    setError(null);
    try {
      await onConfirm(user.id);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : '削除に失敗しました');
      setIsDeleting(false);
    }
  };
  if (!user) return null;
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{
        backgroundColor: '#ffebee',
        color: '#c62828',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <WarningIcon/>
        <Typography component="span" variant="h6">
          ユーザー削除の確認
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          この操作は取り消すことができません。本当に削除してもよろしいですか？
        </Alert>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            削除対象ユーザー情報
          </Typography>

          <Box sx={{
            backgroundColor: '#f5f5f5',
            p: 2,
            borderRadius: 1,
            border: '1px solid #e0e0e0'
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  メールアドレス:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  {user.email}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  ユーザータイプ:
                </Typography>
                <Typography variant="body2">
                  {user.user_type === 'nurse' ? '看護師' : '患者'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  登録日時:
                </Typography>
                <Typography variant="body2">
                  {formatDateWithTime(user.created_at, 'jp')}
                </Typography>
              </Box>

              {user.current_sign_in_at && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    最終ログイン:
                  </Typography>
                  <Typography variant="body2">
                    {formatDateWithTime(user.current_sign_in_at, 'jp')}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        <DialogContentText sx={{ mb: 2 }}>
          削除を実行するには、下記のテキストボックスに
          <Typography component="span" sx={{
            fontWeight: 'bold',
            color: '#c62828',
            mx: 0.5
          }}>
            削除する
          </Typography>
          と入力してください。
        </DialogContentText>

        <TextField
          fullWidth
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder="削除する"
          variant="outlined"
          error={confirmText !== '' && confirmText !== '削除する'}
          helperText={confirmText !== '' && confirmText !== '削除する' ? '正しく入力してください' : ''}
          disabled={isDeleting}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={handleClose}
          disabled={isDeleting}
          color="inherit"
        >
          キャンセル
        </Button>
        <Button
          onClick={handleDelete}
          disabled={confirmText !== '削除する' || isDeleting}
          color="error"
          variant="contained"
        >
          {isDeleting ? '削除中...' : '削除する'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default DeleteUserDialog;
